import throttle from 'lodash/throttle'
import { v4 as uuid } from 'uuid'
import { getCenter, getCenterOfBounds, getDistance } from 'geolib'
import booleanEqual from '@turf/boolean-equal'
import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import {
  getIndexOfNewCoordinate,
  getNearestPointOnLine,
} from '@/lib/mapbox/draw-mode-helpers'
import pubsub from '@/lib/pubsub'
import { findArea, getBestFitUnitOfLength } from '@/lib/math'
import isEqual from 'lodash/isEqual'
import {
  visibleRegion,
  setCursor,
  resetCursor,
  setFeaturesHighlighted,
  clearFeaturesHighlighted,
} from '@/services/map-service'
import { getItem } from '@/plugins/storage'
import FreehandDrawShapeMode from '@/lib/mapbox/freehand-draw-shape-mode'
import { MARKUP_COORDINATES_CHANGED } from '@/lib/mapbox/drawing-events'

/**
 * Throttle interval for noisy events (e.g., onMouseMove)
 */
const THROTTLE_INTERVAL = 60

/**
 * Create/Edit modes for shapes.
 */
class ShapeEditMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(shapeGeoJson, opts = {}) {
    const shapeId = shapeGeoJson.properties.id
    const { canUndo, isCreate } = opts
    if (isCreate) {
      this._initCoordinates(shapeGeoJson)
    }
    const vertexIds = this._generateVertexIds(shapeGeoJson)
    const distanceLabels = this._generateDistanceLabels(shapeGeoJson)

    this._state = {
      shapeId,
      vertexIds,
      distanceLabels,
      draggingVertexIndex: null,
      dragMoveLocation: null,
      borderHoverLocation: null,
      vertexAddLocation: null,
      vertexAddId: null,
      isCreate,
      appendVertex: false,
      handleUndo: canUndo ? this._handleUndo.bind(this) : undefined,
      undoQueue: opts.undoQueue || [],
    }

    if (canUndo) {
      pubsub.subscribe('markupUndo', this._state.handleUndo)
    }

    this._mouseMove = throttle((event) => {
      this._handleMouseMove(event)
    }, THROTTLE_INTERVAL)
  }

  /**
   * @inheritdoc
   */
  onTearDown() {
    resetCursor()
    if (this._state.handleUndo) {
      pubsub.unsubscribe('markupUndo', this._state.handleUndo)
    }
  }

  /**
   * @inheritDoc
   */
  onMouseDown(event) {
    const featureId = this.getFeatureAtPoint(event.point)?.id
    const vertexIds = this._state.vertexIds
    const isAltPressed = event.originalEvent.altKey

    this._state.visibleRegionMouseDown = visibleRegion()

    if (isAltPressed && vertexIds.includes(featureId)) {
      this._handleRemoveVertex(featureId)
      return
    } else if (vertexIds.includes(featureId)) {
      const coordinate = [event.lngLat.lng, event.lngLat.lat]
      this._startDragVertex(coordinate, featureId)
      return
    }

    if (this._isHoveringBorder()) {
      this._state.appendVertex = false
      this._handleAddVertex(event)
    }
  }

  /**
   * @inheritDoc
   */
  onMouseUp(event) {
    const isAltPressed = event.originalEvent.altKey

    if (this._isDraggingVertex()) {
      this._endDraggingVertex()
    } else if (this._state.isCreate && !isAltPressed) {
      const region = visibleRegion()
      const panning = !booleanEqual(region, this._state.visibleRegionMouseDown)
      if (!panning) {
        this._state.appendVertex = true
        this._handleAddVertex(event)
      }
    }
  }

  /**
   * @inheritDoc
   */
  onMouseMove(event) {
    // Call throttled function _mouseMove to prevent performing too many expensive
    // operations on every event. If sufficient delay has elapsed, _handleMouseMove
    // will be called.
    this._mouseMove(event)
  }

  /**
   * Handles the mouse move event
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleMouseMove(event) {
    if (this._isDraggingVertex()) {
      this._handleVertexDrag(event)
      clearFeaturesHighlighted()
      return
    }

    const features = this.getFeaturesAtPoint(event.point)
    const shapeId = this._state.shapeId
    const isHoveringBorder = features.some(
      (feature) => feature.id === shapeId && feature.layer.type === 'line'
    )
    const hoveredVertices = features.filter(
      (feature) => feature.properties.meta === 'vertex'
    )
    const isHoveringVertex = hoveredVertices.length > 0

    if (isHoveringVertex) {
      setCursor('grab')
      setFeaturesHighlighted([hoveredVertices[0]])
    } else {
      resetCursor()
      clearFeaturesHighlighted()
    }

    if (isHoveringBorder && !isHoveringVertex) {
      this._handleHoverPoint(event)
    } else {
      this._removeHoverPoint()
    }
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    const shape = this._formatShape(geoJson)
    const vertices = this._formatVertices(shape)
    const features = [shape, ...vertices]

    const coordinates = geoJson.geometry.coordinates[0]
    if (coordinates?.length < 4) {
      // If we don't yet have a valid shape, draw a line
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: geoJson.geometry.coordinates[0],
        },
        properties: {
          id: `shortened-${geoJson.properties.id}`,
          style: 'solid',
          weight: 'normal',
          color: geoJson.properties.color,
          highlighted: true,
        },
      })
    } else {
      // If we do have a valid shape, add the area label
      const areaLabel = this._generateAreaLabel(geoJson)
      if (areaLabel) {
        features.push(areaLabel)
      }
    }

    if (!geoJson.properties.free_hand) {
      features.push(...this._state.distanceLabels)
    }

    if (this._isHoveringBorder()) {
      const hoverPoint = this._formatHoverPoint()
      features.push(hoverPoint)
    }

    return features
  }

  /**
   * Generates vertex IDs.
   * @param {Object} shapeGeoJson - GeoJSON feature.
   * @return {Array<string>} Array of generated vertex IDs.
   * @private
   */
  _generateVertexIds(shapeGeoJson) {
    const rings = shapeGeoJson.geometry.coordinates
    if (!rings.length) {
      return []
    }
    return rings[0].map(() => uuid())
  }

  /**
   * Generate the distance labels for a given shape
   * @param {Object} [shapeGeoJson] - GeoJSON feature. If not provided, the current feature is used
   * @return {Array<Object>} Array of GeoJSON features for each label
   * @private
   */
  _generateDistanceLabels(shapeGeoJson) {
    const coordinatesList = shapeGeoJson
      ? shapeGeoJson.geometry.coordinates[0]
      : this._getCoordinates()
    const labels = []

    // First and last vertex in GeoJSON polygon are repeated, so ensure we have at least 3 vertices (one segment)
    // to avoid generating a '0' label
    if (coordinatesList?.length >= 3) {
      // Using for loop here since we're grabbing multiple items in the array
      // during each iteration
      for (let i = 0; i < coordinatesList.length - 1; i++) {
        const coordinates = [coordinatesList[i], coordinatesList[i + 1]]
        const distanceLabel = this._generateDistanceLabel(coordinates)
        labels.push(distanceLabel)
      }
    }

    return labels
  }

  /**
   * Generate the distance label between two give points
   * @param {Array<Array>} coordinates - The two coords to measure the distance between
   * @return {Object} GeoJSON feature for the label
   * @private
   */
  _generateDistanceLabel(coordinates) {
    const unitType = getItem('unitType')
    const distance = getDistance(...coordinates)
    const center = getCenter(coordinates)
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
      properties: {
        title: getBestFitUnitOfLength({
          length: distance,
          from: 'meters',
          unitType,
        }),
        meta: 'label',
      },
    }
  }

  /**
   * Generate the area labels for a given shape
   * @param {Object} shapeGeoJson - GeoJSON feature
   * @return {Object} GeoJSON feature for the area
   * @private
   */
  _generateAreaLabel(shapeGeoJson) {
    const coordinatesList = shapeGeoJson.geometry.coordinates[0]
    if (coordinatesList?.length < 3) {
      return null
    }
    const unitType = getItem('unitType')

    const area = findArea(coordinatesList, unitType)
    const center = getCenterOfBounds(coordinatesList)
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.longitude, center.latitude],
      },
      properties: {
        title: `${area.area}${area.UOM}`,
        meta: 'label',
      },
    }
  }

  /**
   * Starts dragging a vertex.
   * @param {Array<number>} coordinate - Longitude, latitude coordinate.
   * @param {string} vertexId - ID of vertex being dragged.
   * @private
   */
  _startDragVertex(coordinate, vertexId) {
    const vertexIds = this._state.vertexIds
    const vertexIndex = vertexIds.findIndex((id) => id === vertexId)
    this.map.dragPan.disable()
    this._state.draggingVertexIndex = vertexIndex
    this._state.dragMoveLocation = coordinate
    this._state.startDragLocation = coordinate
    setCursor('grabbing')
  }

  /**
   * Ends dragging a vertex.
   * @private
   */
  _endDraggingVertex() {
    const { undoQueue, draggingVertexIndex, startDragLocation } = this._state
    undoQueue.push({
      type: 'drag_vertex',
      index: draggingVertexIndex,
      startDragLocation,
    })
    this._updateMarkup()
    this.map.dragPan.enable()
    this._state.draggingVertexIndex = null
    setCursor('grab')
  }

  /**
   * Returns whether a vertex is currently being dragged.
   * @return {boolean} - True if a vertex is being dragged, otherwise false.
   * @private
   */
  _isDraggingVertex() {
    return this._state.draggingVertexIndex !== null
  }

  /**
   * Handles the vertex drag event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleVertexDrag(event) {
    this._state.dragMoveLocation = [event.lngLat.lng, event.lngLat.lat]
    this.updated()
    setCursor('grabbing')
  }

  /**
   * Handles the cursor hover point event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleHoverPoint(event) {
    const coordinates = this._getCoordinates()
    const vertexCoordinate = [event.lngLat.lng, event.lngLat.lat]
    this._state.borderHoverLocation = getNearestPointOnLine(
      coordinates,
      vertexCoordinate
    )
    this.updated()
  }

  /**
   * Handles the adding vertex event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleAddVertex(event) {
    this._state.vertexAddLocation = [event.lngLat.lng, event.lngLat.lat]
    this._state.vertexAddId = uuid()
    this.updated()
  }

  /**
   * Handles the removing vertex event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleRemoveVertex(vertexToRemove) {
    const coordinates = this._getCoordinates()
    const vertexToRemoveIndex = this._state.vertexIds.findIndex(
      (id) => id === vertexToRemove
    )

    // remove coordinate
    const coordinate = coordinates.splice(vertexToRemoveIndex, 1)[0]
    this._state.vertexIds.splice(vertexToRemoveIndex, 1)

    // store action
    this._state.undoQueue.push({
      type: 'remove_vertex',
      index: vertexToRemoveIndex,
      coordinate,
      vertexId: vertexToRemove,
    })

    // update
    this._state.distanceLabels = this._generateDistanceLabels()
    this._updateMarkup()
    this.updated()
    this._removeHoverPoint()
  }

  /**
   * Whether a vertex should be added to the map features.  Helper for use in
   * rendering the line features.
   * @return {boolean} - True if a vertex should be added, otherwise false.
   * @private
   */
  _shouldAddVertex() {
    return (
      this._state.vertexAddLocation !== null && this._state.vertexAddId !== null
    )
  }

  /**
   * Update the given line geoJson if the first/last coordinate is being dragged
   * and corresponding distance labels
   * @param {Object} shapeGeoJson - GeoJSON for the shape feature to add a
   * vertex coordinate to.
   * @private
   */
  _dragFirstOrLastVertex(shapeGeoJson) {
    const { dragMoveLocation } = this._state
    const coords = shapeGeoJson.geometry.coordinates[0]
    const firstVertexIndex = 0
    const lastVertexIndex = coords.length - 1

    if (
      isEqual(coords[firstVertexIndex], dragMoveLocation) &&
      isEqual(coords[lastVertexIndex], dragMoveLocation)
    ) {
      return
    }

    coords.splice(firstVertexIndex, 1, dragMoveLocation)
    coords.splice(lastVertexIndex, 1, dragMoveLocation)

    const firstCoords = [coords[firstVertexIndex], coords[firstVertexIndex + 1]]
    const firstLabel = this._generateDistanceLabel(firstCoords)
    this._state.distanceLabels.splice(firstVertexIndex, 1, firstLabel)
    const lastCoords = [coords[lastVertexIndex], coords[lastVertexIndex - 1]]
    const lastLabel = this._generateDistanceLabel(lastCoords)
    this._state.distanceLabels.pop()
    this._state.distanceLabels.push(lastLabel)
  }

  /**
   * Update the given line geoJson if a middle coordinate is being dragged
   * and corresponding distance labels
   * @param {Object} shapeGeoJson - GeoJSON for the shape feature to add a
   * vertex coordinate to.
   * @private
   */
  _dragMiddleVertex(shapeGeoJson) {
    const { draggingVertexIndex, dragMoveLocation } = this._state
    const coords = shapeGeoJson.geometry.coordinates[0]

    if (isEqual(coords[draggingVertexIndex], dragMoveLocation)) {
      return
    }

    coords.splice(draggingVertexIndex, 1, dragMoveLocation)

    const firstLabelCoords = [
      coords[draggingVertexIndex - 1],
      coords[draggingVertexIndex],
    ]
    const secondLabelCoords = [
      coords[draggingVertexIndex],
      coords[draggingVertexIndex + 1],
    ]
    const firstLabel = this._generateDistanceLabel(firstLabelCoords)
    const secondLabel = this._generateDistanceLabel(secondLabelCoords)
    this._state.distanceLabels.splice(
      draggingVertexIndex - 1,
      2,
      firstLabel,
      secondLabel
    )
  }

  /**
   * Initializes the shape coordinates.
   * @param {Object} geoJson
   * @private
   */
  _initCoordinates(geoJson) {
    // shape coordinates are two-dimensional
    geoJson.geometry.coordinates = [[]]
  }

  /**
   * Adds a vertex coordinate to the given Line GeoJSON, generates a new
   * vertex ID, and updates the distance labels.
   * @param {Object} shapeGeoJson - GeoJSON for the shape feature to add a
   * vertex coordinate to.
   * @private
   */
  _addVertex(shapeGeoJson) {
    const newVertexIndex = this._getAddVertexIndex()
    const coord = this._state.vertexAddLocation
    const coords = shapeGeoJson.geometry.coordinates[0]
    const isFirstVertex = coords.length === 0

    if (isFirstVertex) {
      coords.push(coord, coord)
    } else {
      coords.splice(newVertexIndex, 0, coord)
    }

    const vertexId = this._state.vertexAddId
    this._state.vertexIds.splice(newVertexIndex, 0, vertexId)
    if (isFirstVertex) {
      this._state.vertexIds.splice(1, 0, uuid())
    }

    this._state.undoQueue.push({
      type: 'add_vertex',
      index: newVertexIndex,
    })
    if (!this._state.appendVertex) {
      this._startDragVertex(coord, vertexId)
    }
    this._state.borderHoverLocation = null
    this._state.vertexAddLocation = null
    this._state.vertexAddId = null

    const newLabels = []

    if (newVertexIndex > 0) {
      newLabels.push(
        this._generateDistanceLabel([
          coords[newVertexIndex - 1],
          coords[newVertexIndex],
        ])
      )
    }

    if (newVertexIndex < coords.length - 1 && coords.length >= 4) {
      newLabels.push(
        this._generateDistanceLabel([
          coords[newVertexIndex],
          coords[newVertexIndex + 1],
        ])
      )
    }

    this._state.distanceLabels.splice(newVertexIndex - 1, 1, ...newLabels)
    this._updateMarkup()
  }

  /**
   * Gets the index of the vertex to be added.
   * @return {number} The index position of the new vertex.
   * @private
   */
  _getAddVertexIndex() {
    const vertexCoordinate = this._state.vertexAddLocation
    const coordinates = this._getCoordinates()
    return this._state.appendVertex
      ? Math.max(coordinates.length - 1, 0)
      : getIndexOfNewCoordinate(coordinates, vertexCoordinate)
  }

  /**
   * Removes the cursor hover point.
   * @private
   */
  _removeHoverPoint() {
    if (this._isHoveringBorder()) {
      this._state.borderHoverLocation = null
      this.updated()
    }
  }

  /**
   * Whether the cursor is currently hovering the border.
   * @return {boolean} True if the cursor is hovering the shape border, false
   * otherwise.
   * @private
   */
  _isHoveringBorder() {
    return this._state.borderHoverLocation !== null
  }

  /**
   * Determines whether the first or last vertex are currently being dragged.
   * Helper for maintaining the shape looped border.
   * @return {boolean} True if the first or last vertex is the vertex being
   * dragged, false otherwise.
   * @private
   */
  _isDraggingFirstOrLastVertex() {
    const { draggingVertexIndex, vertexIds } = this._state
    const firstVertexIndex = 0
    const lastVertexIndex = vertexIds.length - 1
    return (
      draggingVertexIndex === firstVertexIndex ||
      draggingVertexIndex === lastVertexIndex
    )
  }

  /**
   * Formats the shape GeoJSON for rendering on the map.
   * @param {Object} shapeGeoJson - Shape feature GeoJSON.
   * @return {Object} Modified shape feature GeoJSON.
   * @private
   */
  _formatShape(shapeGeoJson) {
    if (!this._getFeature()) {
      return shapeGeoJson
    }

    if (this._isDraggingFirstOrLastVertex()) {
      this._dragFirstOrLastVertex(shapeGeoJson)
    } else if (this._isDraggingVertex()) {
      this._dragMiddleVertex(shapeGeoJson)
    }

    if (this._shouldAddVertex()) {
      this._addVertex(shapeGeoJson)
    }

    return shapeGeoJson
  }

  /**
   * Formats a list of vertices for rendering on the map.
   * @param {Object} shapeGeoJson - The shape feature GeoJSON.
   * @return {Array<Object>} List of vertex GeoJSON features to render on the
   * map.
   * @private
   */
  _formatVertices(shapeGeoJson) {
    const vertexIds = this._state.vertexIds
    const coordinatesList = shapeGeoJson.geometry.coordinates[0]
    const vertices = []

    for (let i = 0; i < vertexIds.length; i++) {
      const id = vertexIds[i]
      const coordinates = coordinatesList[i]

      vertices.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates,
        },
        properties: {
          id,
          meta: 'vertex',
          color: shapeGeoJson.properties.color,
        },
      })
    }

    return vertices
  }

  /**
   * Formats a point GeoJSON feature for the cursor hover point.
   * @return {Object} Hover point GeoJSON feature to render on the map.
   * @private
   */
  _formatHoverPoint() {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: this._state.borderHoverLocation,
      },
      properties: {
        meta: 'hover',
      },
    }
  }

  /**
   * Returns the shape feature
   * @return {object} Shape feature
   */
  _getFeature() {
    const shapeId = this._state.shapeId
    return this.getFeature(shapeId)
  }

  /**
   * Gets the shape coordinates
   * @return {array} coordinates
   */
  _getCoordinates() {
    const shapeFeature = this._getFeature()
    return shapeFeature?.geometry.coordinates[0]
  }

  /**
   * Notify listeners when markup is updated
   */
  _updateMarkup() {
    const shapeId = this._state.shapeId
    const shapeFeature = this.getFeature(shapeId)
    const coordinates = shapeFeature.geometry.coordinates
    this.events.emit(MARKUP_COORDINATES_CHANGED, { id: shapeId, coordinates })
  }

  /**
   * Handles the undo button clicked.
   */
  _handleUndo() {
    const command = this._state.undoQueue.pop()
    if (!command) {
      return
    }
    switch (command.type) {
      case 'add_vertex':
        this._undoAddVertex(command)
        break
      case 'drag_vertex':
        this._undoDragVertex(command)
        break
      case 'free_draw':
        this.setMode(FreehandDrawShapeMode)
        break
      case 'remove_vertex':
        this._undoRemoveVertex(command)
    }
  }

  /**
   * Undo add vertex command
   * @param {object} command - Add vertex command
   */
  _undoAddVertex(command) {
    const coordinates = this._getCoordinates()
    if (coordinates.length === 2) {
      coordinates.splice(0, 2)
      this._state.vertexIds.splice(0, this._state.vertexIds.length)
    } else {
      coordinates.splice(command.index, 1)
      this._state.vertexIds.splice(command.index, 1)
    }
    this._state.distanceLabels = this._generateDistanceLabels()
    this._updateMarkup()
    this.updated()
  }

  /**
   * Undo drag vertex command
   * @param {object} command - Drag vertex command
   */
  _undoDragVertex(command) {
    const coordinates = this._getCoordinates()
    // Handle initial vertex (which is duplicated at start and end)
    if (command.index === 0 || command.index === coordinates.length - 1) {
      coordinates.splice(0, 1, command.startDragLocation)
      coordinates.splice(coordinates.length - 1, 1, command.startDragLocation)
    } else {
      coordinates.splice(command.index, 1, command.startDragLocation)
    }
    this._state.distanceLabels = this._generateDistanceLabels()
    this._updateMarkup()
    this.updated()
  }

  /**
   * Undo delete vertex command
   * @param {object} command - Drag vertex command
   */
  _undoRemoveVertex(command) {
    // add back vertex
    const coordinates = this._getCoordinates()

    coordinates.splice(command.index, 0, command.coordinate)
    this._state.vertexIds.splice(command.index, 0, command.vertexId)

    // update
    this._state.distanceLabels = this._generateDistanceLabels()
    this._updateMarkup()
    this.updated()
  }
}

export default ShapeEditMode
